import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiUser3Line, RiLockPasswordLine, RiDeleteBinLine, RiCheckLine,
  RiEyeLine, RiEyeOffLine, RiAlertLine, RiMailLine, RiShieldCheckLine,
  RiCompass3Line, RiPlayCircleLine, RiFeedbackLine,
} from 'react-icons/ri';
import { useAuth } from '../../context/AuthContext';
import { updateProfile, updatePassword, deleteAccount } from '../../api/auth.api';
import { playMark } from '../../components/brand/celebrate';
import { requestTour } from '../../components/tour/useTour';
import { sendFeedback } from '../../api/feedback.api';

/* ══════════════════════════════════════════════════════════════
   ACCOUNT SETTINGS

   The one thing every account needs and this app never had. The
   change-password endpoint existed and was routed from the start —
   nothing ever called it, so a signed-in learner had no way to change
   their own password short of logging out and using the reset-by-email
   flow.

   Email is not editable: it is the account identity and is verified by
   OTP at signup, so changing it means re-running that whole flow.
══════════════════════════════════════════════════════════════ */

function PasswordField({ label, value, onChange, autoComplete }) {
  const [shown, setShown] = useState(false);
  return (
    <label className="acct-field">
      {label}
      <span className="acct-input-wrap">
        <RiLockPasswordLine size={15} />
        <input
          className="ks-input"
          type={shown ? 'text' : 'password'}
          value={value}
          autoComplete={autoComplete}
          onChange={e => onChange(e.target.value)}
        />
        <button type="button" onClick={() => setShown(s => !s)} aria-label={shown ? 'Hide' : 'Show'}>
          {shown ? <RiEyeOffLine size={15} /> : <RiEyeLine size={15} />}
        </button>
      </span>
    </label>
  );
}

export default function AccountSettings() {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();

  const [name, setName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const [fbType, setFbType] = useState('bug');
  const [fbMessage, setFbMessage] = useState('');
  const [fbBusy, setFbBusy] = useState(false);
  const [fbSent, setFbSent] = useState(false);
  const [fbError, setFbError] = useState('');

  const submitFeedback = async (e) => {
    e.preventDefault();
    const text = fbMessage.trim();
    if (!text || fbBusy) return;

    setFbBusy(true); setFbError(''); setFbSent(false);
    try {
      /* The page is captured here rather than server-side: by the time the
         request lands, all the server knows is that something POSTed to
         /api/feedback. Where the learner actually was is the single most
         useful field on a bug report. */
      await sendFeedback({ type: fbType, message: text, page: window.location.pathname });
      setFbMessage('');
      setFbSent(true);
      playMark('bounce');
    } catch (err) {
      setFbError(err.message || 'Could not send that. Please try again.');
    } finally {
      setFbBusy(false);
    }
  };

  const [dangerOpen, setDangerOpen] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);

  const saveName = useCallback(async e => {
    e.preventDefault();
    setSavingName(true);
    setNameMsg(null);
    try {
      const res = await updateProfile({ name: name.trim() });
      updateUser({ name: res.user.name });
      setNameMsg({ ok: true, text: 'Saved.' });
      playMark('nod');
    } catch (err) {
      setNameMsg({ ok: false, text: err.message });
    } finally {
      setSavingName(false);
    }
  }, [name, updateUser]);

  const savePassword = useCallback(async e => {
    e.preventDefault();
    /* Checked here as well as on the server: mistyping the confirmation is
       the common case and does not deserve a round trip. */
    if (next !== confirm) {
      setPwMsg({ ok: false, text: "The two new passwords don't match." });
      return;
    }
    setSavingPw(true);
    setPwMsg(null);
    try {
      await updatePassword({ currentPassword: current, newPassword: next });
      setCurrent(''); setNext(''); setConfirm('');
      setPwMsg({ ok: true, text: 'Password changed. You can keep using this device.' });
      playMark('nod');
    } catch (err) {
      setPwMsg({ ok: false, text: err.message });
    } finally {
      setSavingPw(false);
    }
  }, [current, next, confirm]);

  const confirmDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteMsg(null);
    try {
      await deleteAccount({ password: deletePw });
      logout();
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteMsg(err.message);
      setDeleting(false);
    }
  }, [deletePw, logout, navigate]);

  const isGoogle = user?.authProvider && user.authProvider !== 'local';

  return (
    <div className="page-container acct-page anim-fade-up">
      <header className="acct-head">
        <h1>Account</h1>
        <p>Your sign-in details and what happens to your data.</p>
      </header>

      {/* ── Identity ── */}
      <section className="ks-card acct-card">
        <h2><RiUser3Line size={15} /> Profile</h2>

        <form onSubmit={saveName}>
          <label className="acct-field">
            Display name
            <span className="acct-input-wrap">
              <RiUser3Line size={15} />
              <input className="ks-input" value={name} maxLength={60} onChange={e => setName(e.target.value)} />
            </span>
          </label>

          <label className="acct-field">
            Email
            <span className="acct-input-wrap readonly">
              <RiMailLine size={15} />
              <input className="ks-input" value={user?.email || ''} readOnly disabled />
              {user?.emailVerified && (
                <em className="acct-verified"><RiShieldCheckLine size={13} /> verified</em>
              )}
            </span>
            <small>Your email identifies the account and was verified when you signed up, so it can&apos;t be changed here.</small>
          </label>

          <div className="acct-actions">
            <button className="btn-primary" disabled={savingName || !name.trim() || name.trim() === user?.name}>
              {savingName ? <div className="spinner" /> : <><RiCheckLine size={15} /> Save name</>}
            </button>
            {nameMsg && <span className={`acct-msg${nameMsg.ok ? ' ok' : ''}`}>{nameMsg.text}</span>}
          </div>
        </form>
      </section>

      {/* ── Password ── */}
      <section className="ks-card acct-card">
        <h2><RiLockPasswordLine size={15} /> Password</h2>

        {isGoogle ? (
          <p className="acct-note">
            You sign in with Google, so there is no KitsuSpeak password to change.
            Manage it from your Google account instead.
          </p>
        ) : (
          <form onSubmit={savePassword}>
            <PasswordField label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" />
            <div className="ks-grid-2">
              <PasswordField label="New password" value={next} onChange={setNext} autoComplete="new-password" />
              <PasswordField label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" />
            </div>
            <small className="acct-hint">At least 6 characters.</small>

            <div className="acct-actions">
              <button className="btn-primary" disabled={savingPw || !current || next.length < 6 || !confirm}>
                {savingPw ? <div className="spinner" /> : <><RiCheckLine size={15} /> Change password</>}
              </button>
              {pwMsg && <span className={`acct-msg${pwMsg.ok ? ' ok' : ''}`}>{pwMsg.text}</span>}
            </div>
          </form>
        )}
      </section>

      {/* ── Getting around ── */}
      <section className="ks-card acct-card">
        <h2><RiCompass3Line size={15} /> Getting around</h2>
        <p className="acct-note">
          The guided tour shows you where everything lives and what order to
          learn it in. It runs once when you first sign in — replay it here
          whenever you like.
        </p>
        <button className="btn-ghost" onClick={requestTour}>
          <RiPlayCircleLine size={15} /> Replay the tour
        </button>
      </section>

      {/* ── Feedback ── */}
      <section className="ks-card acct-card">
        <h2><RiFeedbackLine size={15} /> Report a bug or suggest something</h2>
        <p className="acct-note">
          Found something broken, or thought of something that would make this
          better? Tell us here. The page you were on and your browser are sent
          along automatically — that is usually what makes a bug fixable.
        </p>

        <form onSubmit={submitFeedback}>
          <div className="acct-fb-types">
            {[
              { key: 'bug', label: 'Something is broken' },
              { key: 'suggestion', label: 'I have an idea' },
            ].map(t => (
              <button
                key={t.key}
                type="button"
                className={`acct-fb-type${fbType === t.key ? ' active' : ''}`}
                onClick={() => setFbType(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <label className="acct-field">
            <span className="acct-label">
              {fbType === 'bug' ? 'What happened?' : 'What would you change?'}
            </span>
            <textarea
              className="acct-textarea"
              rows={4}
              maxLength={4000}
              value={fbMessage}
              onChange={e => setFbMessage(e.target.value)}
              placeholder={fbType === 'bug'
                ? 'What you did, what you expected, and what happened instead.'
                : 'What you would like, and what it would help you do.'}
            />
            <small className="acct-hint">{fbMessage.length}/4000</small>
          </label>

          {fbError && <p className="acct-error">{fbError}</p>}
          {fbSent && <p className="acct-ok"><RiCheckLine size={14} /> Thanks — that has been sent.</p>}

          <div className="acct-actions">
            <button className="btn-primary" type="submit" disabled={fbBusy || !fbMessage.trim()}>
              {fbBusy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </section>

      {/* ── Danger ── */}
      <section className="ks-card acct-card danger">
        <h2><RiAlertLine size={15} /> Delete account</h2>
        <p className="acct-note">
          This removes your account and everything attached to it — streak, kana and kanji
          mastery, grammar progress, quiz and test history. It cannot be undone.
        </p>

        {!dangerOpen ? (
          <button className="btn-ghost acct-danger-btn" onClick={() => setDangerOpen(true)}>
            <RiDeleteBinLine size={15} /> Delete my account
          </button>
        ) : (
          <div className="acct-danger-panel">
            {!isGoogle && (
              <PasswordField label="Your password" value={deletePw} onChange={setDeletePw} autoComplete="current-password" />
            )}
            <label className="acct-field">
              Type <strong>DELETE</strong> to confirm
              <span className="acct-input-wrap">
                <input className="ks-input" value={deleteTyped} onChange={e => setDeleteTyped(e.target.value)} placeholder="DELETE" />
              </span>
            </label>

            {deleteMsg && <span className="acct-msg">{deleteMsg}</span>}

            <div className="acct-actions">
              <button className="btn-ghost" onClick={() => { setDangerOpen(false); setDeletePw(''); setDeleteTyped(''); setDeleteMsg(null); }}>
                Keep my account
              </button>
              <button
                className="btn-ghost acct-danger-btn"
                disabled={deleting || deleteTyped !== 'DELETE' || (!isGoogle && !deletePw)}
                onClick={confirmDelete}
              >
                {deleting ? <div className="spinner" /> : <><RiDeleteBinLine size={15} /> Permanently delete</>}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
